import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  InlineGrid,
  TextField,
  Button,
  DataTable,
  Text,
  Badge,
  Box,
  SkeletonBodyText,
  EmptyState,
} from '@shopify/polaris';
import reportsApi from '@/services/reports';

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text as="p" variant="bodyMd" tone="subdued">
          {label}
        </Text>
        <Text as="p" variant="headingLg">
          {value}
        </Text>
      </BlockStack>
    </Card>
  );
}

export default function FulfilledByClinic() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());

  const { data: report, isLoading } = useQuery({
    queryKey: ['report-fulfilled-by-clinic', startDate, endDate],
    queryFn: () => reportsApi.getFulfilledByClinic({ startDate, endDate }),
  });

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      await reportsApi.downloadCsv('/reports/fulfilled-by-clinic', { startDate, endDate });
    } finally {
      setExporting(false);
    }
  };

  const rows = (report?.data || []).map((item) => [
    <Button key={item.rentalId} variant="plain" onClick={() => navigate(`/rentals/${item.rentalId}`)}>
      {item.orderNumber || item.rentalId.slice(0, 8)}
    </Button>,
    item.customerName || 'N/A',
    item.clinicName ? (
      <Button key={`clinic-${item.rentalId}`} variant="plain" onClick={() => navigate(`/clinics/${item.clinicId}`)}>
        {item.clinicName}
      </Button>
    ) : '—',
    item.productName || '—',
    item.startDate ? new Date(item.startDate).toLocaleDateString() : '—',
    `$${Number(item.amount).toFixed(2)}`,
    <Badge key={`status-${item.rentalId}`} tone={item.status === 'complete' ? 'success' : 'info'}>
      {item.status}
    </Badge>,
  ]);

  const clinicRows = (report?.summary?.clinicBreakdown || []).map((c) => [
    c.name,
    String(c.count),
    `$${Number(c.revenue).toFixed(2)}`,
  ]);

  return (
    <Page
      title="Fulfilled by Clinic Report"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
    >
      <BlockStack gap="400">
        <Card>
          <InlineStack gap="400" blockAlign="end">
            <Box minWidth="200px">
              <TextField
                label="Start date"
                type="date"
                value={startDate}
                onChange={setStartDate}
                autoComplete="off"
              />
            </Box>
            <Box minWidth="200px">
              <TextField
                label="End date"
                type="date"
                value={endDate}
                onChange={setEndDate}
                autoComplete="off"
              />
            </Box>
            <Button onClick={handleExport} loading={exporting}>
              Export CSV
            </Button>
          </InlineStack>
        </Card>

        {isLoading ? (
          <Card>
            <SkeletonBodyText lines={8} />
          </Card>
        ) : !report || report.data.length === 0 ? (
          <Card>
            <EmptyState
              heading="No fulfilled-by-clinic orders"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>No orders fulfilled by clinics found in this date range.</p>
            </EmptyState>
          </Card>
        ) : (
          <>
            <InlineGrid columns={2} gap="400">
              <StatCard label="Total Orders" value={String(report.summary.totalOrders)} />
              <StatCard label="Total Revenue" value={`$${Number(report.summary.totalRevenue).toFixed(2)}`} />
            </InlineGrid>

            {clinicRows.length > 0 && (
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">By Clinic</Text>
                  <DataTable
                    columnContentTypes={['text', 'numeric', 'text']}
                    headings={['Clinic', 'Orders', 'Revenue']}
                    rows={clinicRows}
                  />
                </BlockStack>
              </Card>
            )}

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Orders</Text>
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
                  headings={['Order', 'Customer', 'Clinic', 'Product', 'Date', 'Amount', 'Status']}
                  rows={rows}
                />
              </BlockStack>
            </Card>
          </>
        )}
      </BlockStack>
    </Page>
  );
}
